export class ProjectValidationError extends Error {
    readonly endpoint: string;
    readonly field: string;

    /** `reason` names the shape problem ("expected string, got number", "empty string")
     *  and never carries the value itself: this message is logged, and a response value
     *  can hold a name, a URL, or a token. Without it a caller only learned which field
     *  was wrong, which sent a real Home-screen outage (a document with an empty title)
     *  to a dead end. */
    constructor(endpoint: string, field: string, reason?: string) {
        super(
            `Invalid response from ${endpoint}: ${field}${reason === undefined ? "" : ` (${reason})`}`,
        );
        this.name = "ProjectValidationError";
        this.endpoint = endpoint;
        this.field = field;
    }
}

export function failValidation(endpoint: string, field: string, reason?: string): never {
    throw new ProjectValidationError(endpoint, field, reason);
}

export function requireRecord(
    value: unknown,
    endpoint: string,
    field: string,
): Record<string, unknown> {
    if (value === null || typeof value !== "object" || Array.isArray(value)) {
        return failValidation(endpoint, field);
    }
    return value as Record<string, unknown>;
}

export function requireArray(value: unknown, endpoint: string, field: string): unknown[] {
    if (!Array.isArray(value)) return failValidation(endpoint, field);
    return value;
}

export function requireString(value: unknown, endpoint: string, field: string): string {
    if (typeof value !== "string")
        return failValidation(endpoint, field, `expected string, got ${typeof value}`);
    if (value.length === 0) return failValidation(endpoint, field, "empty string");
    return value;
}

/** Validates a wire display string. Empty strings are allowed. */
export function requireWireString(value: unknown, endpoint: string, field: string): string {
    if (typeof value !== "string")
        return failValidation(endpoint, field, `expected string, got ${typeof value}`);
    return value;
}

export function requireOptionalWireString(
    value: unknown,
    endpoint: string,
    field: string,
): string | undefined {
    if (value === undefined) return undefined;
    return requireWireString(value, endpoint, field);
}

export function requireNullableWireString(
    value: unknown,
    endpoint: string,
    field: string,
): string | null {
    if (value === null) return null;
    return requireWireString(value, endpoint, field);
}

export function requireOptionalNullableWireString(
    value: unknown,
    endpoint: string,
    field: string,
): string | null | undefined {
    if (value === undefined || value === null) return value;
    return requireWireString(value, endpoint, field);
}

export function requireWireStringArray(value: unknown, endpoint: string, field: string): string[] {
    const values = requireArray(value, endpoint, field);
    for (let index = 0; index < values.length; index += 1) {
        requireWireString(values[index], endpoint, `${field}[${index}]`);
    }
    return values as string[];
}

export interface ProjectRowDropReporter {
    report(label: string, error: ProjectValidationError): void;
}

export function createProjectRowDropReporter(): ProjectRowDropReporter {
    let reported = false;
    return {
        report(label, error) {
            if (reported) return;
            reported = true;
            console.error(`Project API: ${label} row did not load`, error);
        },
    };
}

function isIdentityField(field: string): boolean {
    return (
        field === "file" ||
        field === "id" ||
        field === "document_type" ||
        field.endsWith("_id") ||
        field.endsWith("Id")
    );
}

function isListRowIdentityError(error: unknown, rowField: string): error is ProjectValidationError {
    if (!(error instanceof ProjectValidationError)) return false;
    if (error.field === rowField) return true;
    const prefix = `${rowField}.`;
    if (!error.field.startsWith(prefix)) return false;
    const field = error.field.slice(error.field.lastIndexOf(".") + 1);
    return isIdentityField(field);
}

export function validateResponseListRows<T>(
    rows: unknown[],
    endpoint: string,
    label: string,
    validate: (value: unknown, field: string) => T,
    reporter: ProjectRowDropReporter = createProjectRowDropReporter(),
    fieldPrefix = "",
): T[] {
    let validRows: T[] | undefined;
    for (let index = 0; index < rows.length; index += 1) {
        const field = `${fieldPrefix}[${index}]`;
        try {
            const row = validate(rows[index], field);
            validRows?.push(row);
        } catch (error) {
            if (!isListRowIdentityError(error, field)) throw error;
            reporter.report(label, error);
            if (validRows === undefined) {
                validRows = rows.slice(0, index) as T[];
            }
        }
    }
    return validRows ?? (rows as T[]);
}

export function requireNullableString(
    value: unknown,
    endpoint: string,
    field: string,
): string | null {
    if (value === null) return null;
    return requireString(value, endpoint, field);
}

export function requireBoolean(value: unknown, endpoint: string, field: string): boolean {
    if (typeof value !== "boolean") return failValidation(endpoint, field);
    return value;
}

export function requireFiniteNumber(value: unknown, endpoint: string, field: string): number {
    if (typeof value !== "number" || !Number.isFinite(value)) {
        return failValidation(endpoint, field);
    }
    return value;
}

export function requireNonNegativeInteger(value: unknown, endpoint: string, field: string): number {
    const number = requireFiniteNumber(value, endpoint, field);
    if (!Number.isInteger(number) || number < 0) return failValidation(endpoint, field);
    return number;
}

export function optionalField(record: Record<string, unknown>, key: string): unknown {
    return Object.prototype.hasOwnProperty.call(record, key) ? record[key] : undefined;
}
